// / Define routes that don't require authentication

export const routePermissions = {
    public: [
        "/__nuxt_error",
        '/api/_auth',
        '/api/_auth/session',
        '/api/auth/login', // If you have API endpoints for auth
        '/api/auth/register',
        '/api/auth/slack',
        '/api/auth/user/get-invitation-from-code',
        '/api/auth/user/log-in',
        '/api/auth/user/register',
        '/api/auth/user/session',
        '/api/auth/logout',
        '/forgot-password',
        '/contact-support/problem',
        '/contact-support',
        '/login',
        '/login/register',
        '/no-access',
        '/register',
        '/share',
        '/',
        '/cms',
        '/.well-known/appspecific/com.chrome.devtools.json',
        '/icons*',
        '/maintenance',
    ],

    guest: [],
    user: [
        '/dashboard',
        '/image-gallery',
        '/video-gallery',
        '/files-gallery',
        '/audio-gallery',
        '/bucket',
        '/api/buckets',
        '/api/bucketContent',
        '/api/config/client',
        '/api/files',
        '/api/notification',
        '/api/references',
        '/api/support/whatsapp',
        '/api/share/image',
        '/api/share/media',
        '/profile',
        '/api/users/generate_new_token',
        '/api/users/get-self',
        '/api/users/updateSelf',
    ],
    admin: [
        '/admin',
        '/admin/settings',
        '/activity',
        '/api/users',
        '/api/config',
        '/api/buckets/reorder',
        '/api/auth/user/create-invitation',
        '/api/buckets/init-defaults',
        '/api/activity',
        '/api/maintenance',
    ]
}

const LOCALE_PREFIXES = ['en', 'nl']

// Strip locale prefix from path if present
export function stripLocalePrefix(path: string): string {
    for (const locale of LOCALE_PREFIXES) {
        if (path === `/${ locale }`) return '/'
        if (path.startsWith(`/${ locale }/`)) return path.slice(locale.length + 1)
    }
    return path
}

export function matchesRoute(path: string, routes: readonly string[]): boolean {
    return routes.some(route => {
        if (route.endsWith('*')) {
            const prefix = route.slice(0, -1) // Remove the trailing *
            return path.startsWith(prefix)
        }
        return path === route || (path !== '/' && path.startsWith(route + '/'))
    })
}

export type RequiredRole = 'public' | 'guest' | 'user' | 'admin'

export type RoutePermissions = Partial<Record<RequiredRole, readonly string[]>>

const ROLE_MATCH_ORDER: RequiredRole[] = ['admin', 'public', 'guest', 'user']

function getConfiguredRole(path: string, extraRoutes: RoutePermissions): RequiredRole | null {
    let bestMatch: { role: RequiredRole, routeLength: number } | null = null

    for (const role of ROLE_MATCH_ORDER) {
        for (const route of [...routePermissions[role], ...(extraRoutes[role] ?? [])]) {
            if (!matchesRoute(path, [route])) continue
            if (!bestMatch || route.length > bestMatch.routeLength) {
                bestMatch = { role, routeLength: route.length }
            }
        }
    }

    return bestMatch?.role ?? null
}

// `extraRoutes` comes from app.config `cms.routePermissions`, so a site extending this layer can
// register its own routes. Longest match wins; ties go to ROLE_MATCH_ORDER, so a site listing an
// existing admin route as public does not open it up.
export function getRequiredRole(path: string, method = 'GET', extraRoutes: RoutePermissions = {}): RequiredRole | null {
    const normalizedPath = stripLocalePrefix(path)
    const normalizedMethod = method.toUpperCase()

    // Only delivery and deliberately minimal metadata are public. Keeping this
    // method-aware prevents PATCH/DELETE/POST routes below the same prefix from
    // inheriting public access.
    if (normalizedMethod === 'GET' && (
            /^\/api\/files\/image\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/image\/[a-fA-F0-9]{24}\/metadata$/.test(normalizedPath)
            || /^\/api\/files\/image\/thumbnail\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/share\/image\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/share\/image\/[a-fA-F0-9]{24}\/metadata$/.test(normalizedPath)
            || /^\/api\/files\/video\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/video\/thumbnail\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/media\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/media\/[a-fA-F0-9]{24}\/metadata$/.test(normalizedPath)
            || /^\/api\/files\/media\/thumbnail\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/file\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/file\/[a-fA-F0-9]{24}\/metadata$/.test(normalizedPath)
            || /^\/api\/files\/audio\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || /^\/api\/files\/audio\/[a-fA-F0-9]{24}\/metadata$/.test(normalizedPath)
            || /^\/api\/files\/audio\/[a-fA-F0-9]{24}\/variant\/(mono|channel-\d+)$/.test(normalizedPath)
            || /^\/api\/share\/[a-fA-F0-9]{24}$/.test(normalizedPath)
            || normalizedPath === '/api/share/config'
            // Every visitor needs the mode for the banner and the maintenance page; changing it is admin-only.
            || normalizedPath === '/api/maintenance'
    )) return 'public'

    if (
            normalizedPath === '/api/files/image/migrate'
            || /^\/api\/files\/image\/[a-fA-F0-9]{24}\/regenerate$/.test(normalizedPath)
            || normalizedPath === '/api/files/video/migrate'
            || normalizedPath === '/api/files/video/processing-status'
            || normalizedPath === '/api/files/video/retry'
    ) return 'admin'

    const configuredRole = getConfiguredRole(normalizedPath, extraRoutes)
    if (configuredRole) return configuredRole

    // Pages retain the authenticated-user fallback. APIs must be registered
    // explicitly so a new privileged endpoint cannot silently become user-accessible.
    if (normalizedPath === '/api' || normalizedPath.startsWith('/api/')) return null
    return 'user'
}
